import React, { useState } from 'react';
import { Grid, CircularProgress, Alert } from '@mui/material';

import {
  ProjectCard,
  ProjectColumn,
  ProjectTabs,
  ProjectHeader,
  ProjectContextMenu,
  DeclineProjectModal,
  DeclineConfirmationModal,
  EBadgeModal,
  MissingRequirementsModal,
} from './components';
import { EmptyState } from './components/EmptyState';
import Header from 'modules/views/shared/Header';
import Pagination from 'modules/views/shared/Pagination';
import FullscreenLoader from 'modules/views/shared/FullscreenLoader';

import { useProjectList } from './hooks/useProjectList';
import { useMissingRequirements } from './hooks/useMissingRequirements';

import { Project, TabIndex } from './types';
import { useStyles } from './styles';
import { useProjectActions } from './hooks/useProjectActions';
import { useTranslation } from 'react-i18next';

const ProjectList = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showDeclineConfirmationModal, setShowDeclineConfirmationModal] = useState(false);
  const [showEBadgeModal, setShowEBadgeModal] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<any[]>([]);
  const {
    showModal: showMissingRequirementsModal,
    isLoading: isLoadingRequirements,
    requirementData,
    handleView: handleViewMissingRequirements,
    handleClose: handleMissingRequirementsClose,
  } = useMissingRequirements();

  const {
    projectList,
    projectCounts,
    groupedProjects,
    isLoading,
    error,
    activeTab,
    page,
    handleTabChange,
    handlePageChange,
    paginationInfo,
    handleSortChange,
    sortType,
    refreshProjects,
    isWorker,
  } = useProjectList();
  const {
    isLoading: isProjectActionLoading,
    onViewProfile,
    onViewDocuments,
    onDecline,
    onAddEmployer,
    onRegistration,
  } = useProjectActions({
    selectedProject,
    refreshProjects,
    onDeclineSuccess: () => {
      setSelectedCompanies([]);
      setShowDeclineConfirmationModal(false);
      handleMenuClose();
    },
  });
  const { t } = useTranslation();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, project: Project) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleEBadgeModalClose = () => {
    setShowEBadgeModal(false);
    setSelectedProject(null);
    setAnchorEl(null);
  };

  const handleViewEBadge = async () => {
    setShowEBadgeModal(true);
  };

  const handleDeclineModalClose = () => {
    setShowDeclineModal(false);
    setSelectedCompanies([]);
  };

  const handleDeclineConfirm = async () => {
    if (selectedCompanies.length > 0) {
      setShowDeclineModal(false);
      setShowDeclineConfirmationModal(true);
    }
  };

  const onPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handlePageChange(value);
  };

  const onDeclinePress = () => {
    if (selectedProject?.companies?.length === 1) {
      const selectedCompany = selectedProject.companies?.map(company => ({
        label: company?.company?.name || '',
        value: company?.company?.id || '',
      }));
      setSelectedCompanies(selectedCompany);
      setShowDeclineConfirmationModal(true);
    } else {
      setShowDeclineModal(true);
    }
  };

  const getFilteredProjects = (): Project[] => {
    switch (activeTab) {
      case TabIndex.PENDING:
        return groupedProjects?.pendingProjects || [];
      case TabIndex.PAST:
        return groupedProjects?.pastProjects || [];
      case TabIndex.CURRENT:
        return groupedProjects?.currentProjects || [];
      case TabIndex.ALL:
        return projectList;
      default:
        return projectList;
    }
  };

  const renderAllProjectsContent = () => {
    if (projectList?.length === 0) {
      return <EmptyState activeTab={activeTab} />;
    }

    const { pendingProjects, pastProjects, currentProjects } = groupedProjects;
    return (
      <Grid container spacing={3} className={classes.projectsGrid} data-testid="project-list-grid">
        <ProjectColumn projects={pendingProjects} projectStatus={0} onMenuClick={handleMenuClick} onViewMissingRequirements={handleViewMissingRequirements} />
        <ProjectColumn projects={currentProjects} projectStatus={1} onMenuClick={handleMenuClick} onViewMissingRequirements={handleViewMissingRequirements} />
        <ProjectColumn projects={pastProjects} projectStatus={3} onMenuClick={handleMenuClick} onViewMissingRequirements={handleViewMissingRequirements} />
      </Grid>
    );
  };

  const renderFilteredProjectsContent = () => {
    const filteredProjects = getFilteredProjects();

    if (filteredProjects.length === 0) {
      return <EmptyState activeTab={activeTab} />;
    }

    return (
      <Grid container spacing={3} className={classes.projectsGrid} data-testid="project-list-grid">
        {filteredProjects.map(project => (
          <Grid item xs={12} sm={6} md={4} key={project?.project?.id}>
            <ProjectCard project={project} onMenuClick={handleMenuClick} onViewMissingRequirements={handleViewMissingRequirements} />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <div className={`${classes.container} ${!isWorker ? classes.containerWithWarning : ''}`}>
      <Header styleClass={classes.mainHeader} showOnlineWarning={!isWorker} />
      {/* Header */}
      <ProjectHeader />

      {/* Tabs */}
      <ProjectTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        projectCounts={projectCounts}
        onSortChange={handleSortChange}
        sortType={sortType}
      />

      {/* Loading State */}
      {isLoading && (
        <div className={classes.loadingContainer} data-testid="project-list-loader">
          <CircularProgress />
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className={classes.errorContainer} data-testid="project-list-error">
          <Alert severity="error">{error}</Alert>
        </div>
      )}

      {/* Projects Content */}
      <div className={classes.content} data-testid="project-list-content">
        {!isLoading && !error && (
          <>{activeTab === TabIndex.ALL ? renderAllProjectsContent() : renderFilteredProjectsContent()}</>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !error && paginationInfo && paginationInfo.totalResults > 0 && (
        <div className={classes.paginationWrapper}>
          <Pagination
            count={Math.ceil(paginationInfo.totalResults / paginationInfo.pageSize)}
            page={page}
            onChange={onPageChange}
            siblingCount={1}
            boundaryCount={3}
            testId="project-list-pagination"
          />
        </div>
      )}

      {/* Fullscreen Loader */}
      {isProjectActionLoading && <FullscreenLoader transparent={true} testId="project-action-loader" />}

      {/* Context Menu */}
      <ProjectContextMenu
        anchorEl={anchorEl}
        selectedProject={selectedProject}
        onClose={handleMenuClose}
        onViewProfile={onViewProfile}
        onViewDocuments={onViewDocuments}
        onDecline={onDeclinePress}
        onAddEmployer={onAddEmployer}
        onRegistration={onRegistration}
        onViewEBadge={handleViewEBadge}
      />

      {/* E-Badge Modal */}
      <EBadgeModal
        isVisible={showEBadgeModal}
        onClose={handleEBadgeModalClose}
        selectedProject={selectedProject}
        isWorker={isWorker}
      />

      {/* Decline Project Modal */}
      <DeclineProjectModal
        isVisible={showDeclineModal}
        selectedProject={selectedProject}
        selectedCompanies={selectedCompanies}
        isLoading={isProjectActionLoading}
        onClose={handleDeclineModalClose}
        onConfirm={handleDeclineConfirm}
        onCompanyChange={values => setSelectedCompanies(values)}
      />
      <DeclineConfirmationModal
        isVisible={showDeclineConfirmationModal}
        onClose={() => setShowDeclineConfirmationModal(false)}
        selectedCompanies={selectedCompanies}
        onDecline={() => {
          onDecline(selectedCompanies);
        }}
      />

      {/* Missing Requirements Modal */}
      <MissingRequirementsModal
        isVisible={showMissingRequirementsModal}
        onClose={handleMissingRequirementsClose}
        isLoading={isLoadingRequirements}
        requirementData={requirementData}
      />
    </div>
  );
};

export default ProjectList;
